import { type ParsedUrlQuery } from 'node:querystring';

import { type Feature, FeatureSource } from '../types/api/product';

import { type CookieStore } from '@/utilities/cookieStore';

/**
 * List of all recognized feature names.
 */
export const FEATURE_NAMES: string[] = [
  'EX1082', // Personalization of products on PLP
  'useNewProductUrls', // Use new product URL patterns (e.g. /d/product-name-1234567890-red)
];

/**
 * Represents a set of features with helper methods for access and use in graphql queries.
 */
export interface Features {
  readonly list: Feature[];

  has(name: string): boolean;
  get(name: string): Feature | undefined;

  toGraphQLVariables(): { features: Feature[] };
  toGraphQLInputLiteral(): string;

  merge(other: Features): Features;

  ifEmpty(fallback: Features | undefined): Features | undefined;
}

/**
 * Helper to escape a JS string for a GraphQL inline literal.
 */
const escapeGraphQLString = (value: string): string => {
  const escaped = value.replaceAll('\\', '\\\\').replaceAll('"', '\\"');
  return `"${escaped}"`;
};

const toGraphQLInputLiteral = (features: Feature[]): string => {
  if (features.length === 0) return '[]';

  const items = features.map(feature => {
    const namePart = `name: ${escapeGraphQLString(feature.name)}`;
    const groupPart =
      feature.group === undefined ? '' : `, group: ${escapeGraphQLString(feature.group)}`;
    const sourcePart = feature.source === undefined ? '' : `, source: ${feature.source}`;
    return `{ ${namePart}${groupPart}${sourcePart} }`;
  });

  return `[${items.join(', ')}]`;
};

/**
 * Features constructor & normalization.
 */
export const createFeatures = (from: Feature[], whitelist: string[] = FEATURE_NAMES): Features => {
  const byName = new Map<string, Feature>();

  for (const f of from) {
    if (!whitelist?.includes(f.name)) continue;

    // A trusted feature shouldn't be overridden by a same-named filter experiment regardless of merge order
    const existing = byName.get(f.name);
    if (existing?.source === FeatureSource.Default && f.source === FeatureSource.FilterExperiment) {
      continue;
    }

    const group = f.group == null || f.group === '' ? undefined : f.group;

    byName.set(f.name, {
      name: f.name,
      group,
      source: f.source,
    });
  }

  const list = Array.from(byName.values());

  const features: Features = {
    list,

    has(name) {
      return byName.has(name);
    },

    get(name) {
      return byName.get(name);
    },

    toGraphQLVariables() {
      return { features: [...list] };
    },

    toGraphQLInputLiteral() {
      return toGraphQLInputLiteral(list);
    },

    merge(other) {
      // this.list and other.list are each already filtered (by createFeatures, below).
      // Re-filtering by only one whitelist would remove valid entries from other.list.
      const combined = [...this.list, ...other.list];
      return createFeatures(
        combined,
        combined.map(feature => feature.name),
      );
    },

    ifEmpty(fallback) {
      if (this.list.length === 0) {
        return fallback;
      }
      return this;
    },
  };

  return features;
};

/**
 * Parse features from URLSearchParams.
 * Keys in the query string are interpreted directly as feature names,
 * and only names present in string[] are accepted.
 */
export const featuresFromSearchParams = (
  params: ParsedUrlQuery,
  whitelist: string[] = FEATURE_NAMES,
): Features => {
  const features: Feature[] = whitelist
    .map(name => {
      const groups = params[name];
      if (groups === undefined) return undefined;
      const group = Array.isArray(groups) ? groups.join(',') : String(groups);
      return { name, group, source: FeatureSource.Default };
    })
    .filter(f => f !== undefined);

  return createFeatures(features, whitelist);
};

/**
 * Parse features from cookies represented as a plain object.
 * Cookie names are interpreted directly as feature names and filtered by FEATURE_NAMES.
 */
export const featuresFromCookies = (
  cookies: CookieStore,
  whitelist: string[] = FEATURE_NAMES,
): Features => {
  const features = whitelist
    .map(name => {
      const group = cookies.get(name);
      if (group !== undefined) {
        return { name, group, source: FeatureSource.Default };
      }
    })
    .filter(f => f !== undefined);

  if (features && features.length > 0) {
    return createFeatures(features, whitelist);
  }

  return createFeatures([]);
};

export const EMPTY_FEATURES: Features = createFeatures([]);
